import { createSlice, configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {IIndexable} from "../../../domain/interfaces";
import {getJSONFromHtml} from "../../../domain/common";
import {
    SbsActiveTier,
    SoftwareCodeAlert,
    SoftwareCodesReponse,
    SubscriptionPlanModel,
    SubscriptionsVM
} from "../interfaces";
import { PluginSortStatusEnum} from "../enums";

const _subscriptionsPageData = (getJSONFromHtml(document.getElementById('subscriptions-data')) as SubscriptionsVM);
const _imgCdnDomain = (document.getElementById('img-cdn-domain')?.textContent || '')?.trim().toLowerCase();
const _softwareCodesAlerts = (getJSONFromHtml(document.getElementById('software-codes-alerts-data')) as SoftwareCodeAlert[]);

/**
 * Json data on shared layout
 * In use for querystrings (plugins=STAS_ , plugins=COMM_ , plugins=STAS_COMM_ ) logic.
 * SP parse these querystrings to a key, the key is the itemOrder of each SbsActiveTier.
 *
 * More info in Jira
 * */
const _sbsActiveTiers = (getJSONFromHtml(document.getElementById('sbs-active-tiers')) as SbsActiveTier[]);
/**
 * Json data from template's code mirror
 * This is the start of the sbs alert sentence
 * The PageData.sbsIncludedIn is the value for SbsActiveTier.itemOrder (the rest of the alert sentence)
 *
 * More info in Jira
 * */
//not in use!
//const _softwareCodesAlertSentence = (document.getElementById('software-codes-alert-sentence')?.textContent || '')?.trim();

/**
* In use for querystrings (plugins=STAS_ , plugins=COMM_ , plugins=STAS_COMM_ ) logic
 * Or for querystrings (skus=75, skus=75_819_ ) logic
* SP parse these querystrings to this value ()
 * This value is itemOrder from sbs-active-tiers json data from shared layout.
 * More info in Jira
* */
const _softwareCodesReponse = (getJSONFromHtml(document.getElementById('software-codes-reponse-data')) as SoftwareCodesReponse);
//_softwareCodesReponse.thumb = 'https://media.wavescdn.com/images/products/plugins/600/waves-stream.png';
//console.log('_softwareCodesReponse: ',_softwareCodesReponse);


/**
 *
 * ShowPluginImageSortIncludesID : plugin identifier (by SortIncludesID) in order to show plugin view ( after clicking the eye icon)
 * When ShowPluginImageSortIncludesID > 0 --> show the plugin view popup for the specific plugin
 * When ShowPluginImageSortIncludesID <= 0 --> hide plugin view
 *
 * PluginViewPosition : the position (CSS) for positioning the plugin view after eye icon click (speech bubble popup)
 *
 * PlanOnMobilePluginsPopup : on mobile, the plugins popup shows data on the clicked plan, PlanOnMobilePluginsPopup is the clicked plan
 *
 * */
export interface StateInterface extends IIndexable{
    PageData: SubscriptionsVM;
    SearchFilterKey: string;
    ImgCdnDomain: string;
    PluginSortStatus: number;
    ShowSortChoices:boolean;
    ShowPluginImageSortIncludesID: -1;
    PluginViewPosition:any;
    IsAnnual:boolean;
    CreditTextPopupShow: boolean;
    IsPriceDifferent:boolean;
    PlanOnMobilePluginsPopup:SubscriptionPlanModel | null;
    SoftwareCodesAlerts:SoftwareCodeAlert[];
    SbsActiveTiers:SbsActiveTier[];
    QueryStringsReponse:SoftwareCodesReponse;
    IsTwoMonthFreePopupShown: boolean;
    PlanSavePopupShown: number | null;
    PlanCostPopupShown: number | null;
}
// initial state
const initialState: StateInterface = {
    PageData:_subscriptionsPageData,
    SearchFilterKey:"",
    ImgCdnDomain:_imgCdnDomain,
    PluginSortStatus:PluginSortStatusEnum.Popular as number,
    ShowSortChoices:false,
    ShowPluginImageSortIncludesID: -1,
    PluginViewPosition:null,
    IsAnnual:true,
    CreditTextPopupShow: false,
    IsPriceDifferent:_subscriptionsPageData?.subscriptionPlans?.some(s=>s.isPriceDifferent === true),
    PlanOnMobilePluginsPopup:null,
    SoftwareCodesAlerts:_softwareCodesAlerts,
    SbsActiveTiers:_sbsActiveTiers,
    QueryStringsReponse:_softwareCodesReponse,
    AnnualPriceDisplayType:"",
    IsTwoMonthFreePopupShown: false,
    PlanSavePopupShown: null,
    PlanCostPopupShown: null,
}
// the main slice
const rootSlice = createSlice({
    name: 'root',
    initialState,
    reducers: {
        main(state, action) {
            return {
                ...state,
                ...action.payload,
            };
        },
    },
});

const store = configureStore({
    reducer: rootSlice.reducer,
});

export const rootActions = rootSlice.actions;
export default store;

/**
 * Infer the `RootState` and `AppDispatch` types from the store itself
 * https://redux.js.org/recipes/usage-with-typescript
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
// https://redux.js.org/recipes/usage-with-typescript
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

